import React from 'react';
import {
  Users,
  UserCheck,
  UserX,
  TrendingUp,
  Building2,
  BarChart3,
  Activity
} from 'lucide-react';
import { ICompanyStatistics } from './CompanyStatisticsPanel';
import { formatNumberValue, removeUnderscoresAndCapitalize } from '../../../utils/miscFunctions';
import { COMPANY_TYPES } from '../../../resources/enums';


interface UserStatisticsPanelProps {
  data?: ICompanyStatistics | null;
  isLoadingUserStatistics: boolean;
}

const UserStatisticsPanel: React.FC<UserStatisticsPanelProps> = ({ data, isLoadingUserStatistics }) => {
  if (isLoadingUserStatistics) {
    return (
      <div className="space-y-6 ">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-64 animate-pulse"></div>
          </div>
        </div>

        {/* Key Metrics Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-darkBg">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                  <div className="h-8 bg-gray-200 rounded w-16 animate-pulse"></div>
                  <div className="h-3 bg-gray-200 rounded w-20 animate-pulse"></div>
                </div>
                <div className="h-12 w-12 bg-gray-200 rounded-2xl animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Three Column Layout Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Bar Chart Skeleton */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-darkBg">
            <div className="flex items-center justify-between mb-6">
              <div className="h-6 bg-gray-200 rounded w-40 animate-pulse"></div>
              <div className="h-5 bg-gray-200 rounded w-12 animate-pulse"></div>
            </div>
            <div className="space-y-4">
              {[...Array(5)].map((_, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="h-4 bg-gray-200 rounded w-8 animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded w-10 animate-pulse"></div>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Middle Column - Table Skeleton */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-darkBg">
            <div className="h-6 bg-gray-200 rounded w-48 mb-4 animate-pulse"></div>
            <div className="space-y-3">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="flex items-center justify-between py-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-gray-200 rounded-full animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
                  </div>
                  <div className="flex space-x-4">
                    <div className="h-4 bg-gray-200 rounded w-8 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-10 animate-pulse"></div>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between py-2 pt-4 border-t">
                <div className="h-4 bg-gray-200 rounded w-12 animate-pulse"></div>
                <div className="flex space-x-4">
                  <div className="h-4 bg-gray-200 rounded w-8 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-10 animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Insights Skeleton */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-darkBg">
            <div className="h-6 bg-gray-200 rounded w-32 mb-4 animate-pulse"></div>
            <div className="space-y-4">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-2xl border border-gray-200 dark:bg-darkBg">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 bg-gray-200 rounded-2xl animate-pulse"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-gray-200 rounded w-20 animate-pulse"></div>
                      <div className="h-5 bg-gray-200 rounded w-24 animate-pulse"></div>
                      <div className="h-3 bg-gray-200 rounded w-32 animate-pulse"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalUsersByType = Object.values(data?.usersByCompanyType || {}).reduce((sum, count) => sum + count, 0);

  const getCompanyTypeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      [COMPANY_TYPES.BROKER]: 'bg-blue-500',
      [COMPANY_TYPES.INSURER]: 'bg-emerald-500',
      [COMPANY_TYPES.AGENCY]: 'bg-violet-500',
      [COMPANY_TYPES.FINANCIER]: 'bg-amber-500',
      [COMPANY_TYPES.INTERNAL]: 'bg-slate-500',
      [COMPANY_TYPES.REPAIR_CENTER]: 'bg-rose-500',
      [COMPANY_TYPES.RIDE_SHARING]: 'bg-indigo-500',
      [COMPANY_TYPES.FUEL_PROVIDER]: 'bg-orange-500',
      [COMPANY_TYPES.AFFILIATE]: 'bg-teal-500',
      [COMPANY_TYPES.FLEET]: 'bg-cyan-500',
      [COMPANY_TYPES.OTHER]: 'bg-gray-400'
    };
    return colors[type] || 'bg-gray-400';
  };


  // Calculate percentage for each type
  const userTypeData = Object.entries(data?.usersByCompanyType || {})
    .map(([type, count]) => ({
      type,
      count,
      percentage: totalUsersByType > 0 ? ((count / totalUsersByType) * 100).toFixed(1) : '0'
    }))
    .sort((a, b) => b.count - a.count);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">User Statistics</h2>
          <p className="text-sm text-gray-500 mt-1 dark:text-gray-200">Overview of platform users and distribution across companies</p>
        </div>
      </div>

      {/* Key User Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Users */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-transparent dark:border dark:border-darkBg-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-200">Total Users</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">{formatNumberValue(totalUsersByType)}</p>
              <p className="text-xs text-gray-500 mt-1 dark:text-gray-200">Across all companies</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-2xl">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Active Users */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-transparent dark:border dark:border-darkBg-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-200">Active Users</p>
              <p className="text-3xl font-bold text-green-600 mt-2 dark:text-green-200">
                {formatNumberValue(data?.userMetrics?.activeUsers)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Currently active</p>
            </div>
            <div className="p-3 bg-green-100 rounded-2xl">
              <UserCheck className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Inactive Users */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-transparent dark:border dark:border-darkBg-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-200">Inactive Users</p>
              <p className="text-3xl font-bold text-orange-600 mt-2 dark:text-orange-200">
                {formatNumberValue(data?.userMetrics?.inactiveUsers)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Not active</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-2xl">
              <UserX className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>

        {/* Average Users Per Company */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-transparent dark:border dark:border-darkBg-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-200">Avg Users/Company</p>
              <p className="text-3xl font-bold text-purple-600 mt-2 dark:text-purple-200">
                {formatNumberValue(data?.userMetrics?.averageUsersPerCompany)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Per company</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-2xl">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Three Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Users by Company Type - Bar Chart Visualization */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-transparent dark:border dark:border-darkBg-border">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">Users by Company Type</h3>
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5 text-gray-600 dark:text-darkBg-mainText" />
              <span className="text-sm text-gray-600 dark:text-darkBg-mainText">{formatNumberValue(totalUsersByType)}</span>
            </div>
          </div>

          {userTypeData.length == 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center space-y-3">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                  <BarChart3 className="h-6 w-6 text-gray-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-darkBg-mainText">No user data available</p>
                  <p className="text-xs text-gray-500 mt-1 dark:text-darkBg-mainText">Users by company type data will appear here</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {userTypeData.map(({ type, count, percentage }) => (
                <div key={type} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded ${getCompanyTypeColor(type)}`}></div>
                      <span className="text-xs font-medium text-gray-700 dark:text-darkBg-mainText">{removeUnderscoresAndCapitalize(type)}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-semibold text-gray-900 dark:text-darkBg-mainText">{count}</span>
                      <span className="text-xs text-gray-500">{percentage}%</span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${getCompanyTypeColor(type)}`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* User Distribution Details Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-transparent dark:border dark:border-darkBg-border">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText mb-4">User Distribution Details</h3>
          <div className="overflow-x-auto">
            {userTypeData.length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center space-y-3">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                    <Users className="h-6 w-6 text-gray-400 dark:text-darkBg-mainText" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-darkBg-mainText">No distribution data available</p>
                    <p className="text-xs text-gray-500 mt-1">User distribution details will appear here</p>
                  </div>
                </div>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-darkBg-border dark:text-darkBg-mainText">
                <thead className="bg-gray-50 dark:bg-darkBg">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-darkBg-mainText uppercase">Type</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-darkBg-mainText uppercase">Users</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-darkBg-mainText uppercase">Share</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 dark:divide-darkBg-border dark:text-darkBg-mainText dark:bg-darkBg">
                  {userTypeData.map(({ type, count, percentage }) => (
                    <tr key={type} className="hover:bg-gray-50 dark:hover:bg-transparent">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className={`w-2 h-2 rounded-full ${getCompanyTypeColor(type)}`}></div>
                          <span className="text-xs text-gray-900 dark:text-darkBg-mainText">{removeUnderscoresAndCapitalize(type)}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium text-gray-900 dark:text-darkBg-mainText">
                        {count}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-right text-xs text-gray-600">
                        {percentage}%
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 dark:bg-darkBg">
                  <tr>
                    <td className="px-3 py-2 text-xs font-bold text-gray-900">Total</td>
                    <td className="px-3 py-2 text-right text-xs font-bold text-gray-900">{totalUsersByType}</td>
                    <td className="px-3 py-2 text-right text-xs font-bold text-gray-900">100%</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>

        {/* User Insights */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 dark:bg-transparent dark:border dark:border-darkBg-border">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText mb-4">User Insights</h3>
          <div className="space-y-4">
            {/* Top Company Type by Users */}
            <div className="p-4 bg-blue-50 rounded-2xl border border-blue-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-500 rounded-2xl">
                  <Building2 className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-blue-700 uppercase">Most Users</p>
                  <p className="text-lg font-bold text-blue-900 mt-1">
                    {userTypeData[0] ? `${removeUnderscoresAndCapitalize(userTypeData[0].type)}s` : '-'}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    {userTypeData[0]?.count || 0} users ({userTypeData[0]?.percentage || 0}%)
                  </p>
                </div>
              </div>
            </div>

            {/* User Activity Status */}
            <div className="p-4 bg-green-50 rounded-2xl border border-green-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-500 rounded-2xl">
                  <Activity className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-green-700 uppercase">User Activity</p>
                  <p className="text-lg font-bold text-green-900 mt-1">
                    {data?.userMetrics.activeUsers}
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    Active users on the platform
                  </p>
                </div>
              </div>
            </div>

            {/* Company Coverage */}
            <div className="p-4 bg-purple-50 rounded-2xl border border-purple-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-500 rounded-2xl">
                  <Users className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-purple-700 uppercase">Company Coverage</p>
                  <p className="text-lg font-bold text-purple-900 mt-1">
                    {Object.keys(data?.companiesByType || {}).length} Types
                  </p>
                  <p className="text-xs text-purple-600 mt-1">
                    Different company types with users
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserStatisticsPanel;
